import React, { useState, useEffect, useRef } from 'react';
import { importEspnPool, importCbsPool, importYahooPool, PoolImportResult } from '../api/api';
import { Loader2, Check } from 'lucide-react';
import { trackEvent, trackRedditEvent } from '../utils/analytics';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';

type Provider = 'espn' | 'cbs' | 'yahoo' | null;

interface ValidationState {
  provider: Provider;
  poolId: string | null;
  error: string | null;
}

interface AddPoolModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdvanced: () => void;
  onSuccess: (poolName: string) => void;
}

const FUN_MESSAGES = [
  'Populating brackets...',
  'Herding llamas...',
  'Pumping up basketballs...',
  'Analyzing matchups...',
  'Counting seeds...',
  'Planting seeds...',
];

const TIMEOUT_MS = 600000; // 10 minutes

function parsePoolUrl(url: string): ValidationState {
  const trimmed = url.trim();
  if (!trimmed) {
    return { provider: null, poolId: null, error: null };
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return { provider: null, poolId: null, error: null };
  }

  const hostname = parsed.hostname.toLowerCase();

  if (hostname.includes('espn.com')) {
    const groupId = parsed.searchParams.get('id');
    if (groupId && groupId.length > 0) {
      return { provider: 'espn', poolId: groupId, error: null };
    }
    return { provider: 'espn', poolId: null, error: 'Could not find a pool ID in this URL. Make sure you\'ve selected a pool.' };
  }

  if (hostname.includes('cbssports.com')) {
    // Extract pool ID from path: /pools/<id>/standings or /pools/<id>
    const pathMatch = parsed.pathname.match(/\/pools\/([^/]+)/);
    if (pathMatch && pathMatch[1]) {
      return { provider: 'cbs', poolId: pathMatch[1], error: null };
    }
    return { provider: 'cbs', poolId: null, error: 'Could not find a pool ID in this URL. Make sure you\'ve selected a pool.' };
  }

  if (hostname.includes('yahoo.com')) {
    // Extract group ID from path: /mens-basketball-bracket/group/<id>
    const pathMatch = parsed.pathname.match(/\/group\/(\d+)/);
    if (pathMatch && pathMatch[1]) {
      return { provider: 'yahoo', poolId: pathMatch[1], error: null };
    }
    return { provider: 'yahoo', poolId: null, error: 'Could not find a group ID in this URL. Make sure you\'ve selected a group.' };
  }

  return { provider: null, poolId: null, error: null };
}

const AddPoolModal: React.FC<AddPoolModalProps> = ({
  isOpen,
  onClose,
  onAdvanced,
  onSuccess,
}) => {
  const [url, setUrl] = useState('');
  const [validation, setValidation] = useState<ValidationState>({ provider: null, poolId: null, error: null });
  const [isImporting, setIsImporting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [result, setResult] = useState<PoolImportResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const messageIndexRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Validate URL as user types
  useEffect(() => {
    setValidation(parsePoolUrl(url));
  }, [url]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setUrl('');
      setValidation({ provider: null, poolId: null, error: null });
      setIsImporting(false);
      setStatusMessage('');
      setResult(null);
      setErrorMessage(null);
      messageIndexRef.current = 0;
    }
  }, [isOpen]);

  const startFunMessages = (provider: Provider) => {
    const providerName = provider === 'espn' ? 'ESPN' : provider === 'yahoo' ? 'Yahoo' : 'CBS';
    setStatusMessage(`Connecting to ${providerName}...`);
    messageIndexRef.current = 0;

    timerRef.current = setInterval(() => {
      setStatusMessage(FUN_MESSAGES[messageIndexRef.current % FUN_MESSAGES.length]);
      messageIndexRef.current++;
    }, 3000);
  };

  const stopTimers = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleSubmit = async () => {
    if (!validation.provider || !validation.poolId) return;

    setIsImporting(true);
    setResult(null);
    setErrorMessage(null);
    startFunMessages(validation.provider);

    // Set timeout
    timeoutRef.current = setTimeout(() => {
      stopTimers();
      setIsImporting(false);
      setErrorMessage('Import timed out. The pool may be too large or the service may be unavailable. Please try again.');
    }, TIMEOUT_MS);

    try {
      let importResult: PoolImportResult;
      if (validation.provider === 'espn') {
        importResult = await importEspnPool(validation.poolId);
      } else if (validation.provider === 'yahoo') {
        importResult = await importYahooPool(validation.poolId);
      } else {
        importResult = await importCbsPool(validation.poolId);
      }

      stopTimers();
      setIsImporting(false);

      if (importResult.success) {
        setResult(importResult);
        trackEvent('pool_import', { provider: validation.provider! });
        trackRedditEvent('Lead');
      } else {
        setErrorMessage('Import failed. Please check the URL and try again.');
      }
    } catch (err: any) {
      stopTimers();
      setIsImporting(false);
      const msg = err.message || 'An unexpected error occurred.';
      if (msg.includes('Invalid CBS pool ID') || msg.includes('error connecting to CBS')) {
        setErrorMessage('Unable to connect to CBS Sports. Please verify your pool URL is correct and try again after the tournament begins on March 19th at 12:00p EDT!');
      } else {
        setErrorMessage(msg);
      }
    }
  };

  const handleOk = () => {
    if (result) {
      onSuccess(result.pool_id);
    }
    onClose();
  };

  const handleAdvanced = () => {
    onClose();
    onAdvanced();
  };

  const isValid = validation.provider !== null && validation.poolId !== null;
  const showOk = result !== null || errorMessage !== null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !isImporting) onClose(); }}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] flex flex-col p-0 gap-0"
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Header */}
        <DialogHeader className="px-5 pt-5 pb-4 border-b">
          <div className="flex items-center gap-4">
            <DialogTitle className="text-xl">Add a Pool</DialogTitle>
            <Button variant="outline" size="sm" className="text-xs" onClick={handleAdvanced}>
              Advanced
            </Button>
          </div>
          <DialogDescription>
            Import your bracket pool from ESPN, CBS, or Yahoo.
          </DialogDescription>
        </DialogHeader>

        {/* Body */}
        <div className="px-5 py-5 flex-1 overflow-y-auto">
          {/* Instructions */}
          <div className="mb-5 space-y-2">
            <p className="text-sm leading-relaxed text-foreground">
              ESPN, CBS, and Yahoo bracket pools are supported.
            </p>
            <p className="text-sm leading-relaxed text-foreground">
              <strong>For ESPN:</strong> Go to{' '}
              <a
                href="https://fantasy.espn.com/games/tournament-challenge-bracket-2026/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                ESPN Tournament Challenge
              </a>
              , select your pool, and copy the URL from your browser.
            </p>
            <p className="text-sm leading-relaxed text-foreground">
              <strong>For CBS:</strong> Go to{' '}
              <a
                href="https://picks.cbssports.com/college-basketball/ncaa-tournament/bracket/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                CBS Bracket Pools
              </a>
              , select your pool, and copy the URL from your browser.
            </p>
            <p className="text-sm leading-relaxed text-foreground">
              <strong>For Yahoo:</strong> Go to{' '}
              <a
                href="https://tournament.fantasysports.yahoo.com/mens-basketball-bracket/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Yahoo Bracket Mayhem
              </a>
              , select your group, and copy the URL from your browser.
            </p>
          </div>

          {/* URL Input */}
          <Input
            type="text"
            placeholder="Paste your ESPN, CBS, or Yahoo pool URL here"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={isImporting || showOk}
          />

          {/* Validation Feedback */}
          <div className="min-h-[24px] mt-2 mb-4">
            {!isImporting && !showOk && (
              <>
                {validation.error ? (
                  <span className="text-sm text-destructive">{validation.error}</span>
                ) : validation.provider && validation.poolId ? (
                  <span className="text-sm text-success font-medium">
                    <Check className="inline h-4 w-4 mr-1" />
                    {validation.provider === 'espn' ? 'ESPN' : validation.provider === 'yahoo' ? 'Yahoo' : 'CBS'} pool detected
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">Paste an ESPN, CBS, or Yahoo pool URL to get started</span>
                )}
              </>
            )}
          </div>

          {/* Submit Button */}
          {!showOk && (
            <Button
              className="w-full"
              onClick={handleSubmit}
              disabled={!isValid || isImporting}
            >
              Add My Pool
            </Button>
          )}

          {/* Progress Area */}
          {isImporting && (
            <div className="mt-5 flex justify-center">
              <div className="text-center">
                <div className="flex items-center justify-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">{statusMessage}</span>
                </div>
                {validation.provider === 'cbs' && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Importing brackets now. For very big pools, this might take 10+ minutes — it will continue to work in the background if you close this page.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Success Result */}
          {result && (
            <div className="mt-5 rounded-md border border-success/50 bg-success/10 p-4 flex gap-3 items-start">
              <Check className="h-5 w-5 text-success mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-bold text-[15px] text-foreground mb-1">{result.pool_id}</div>
                <div className="text-sm text-foreground">
                  {result.entries_imported} bracket{result.entries_imported !== 1 ? 's' : ''} imported
                </div>
                {result.truncated && result.total_entries && (
                  <div className="text-sm text-amber-600 dark:text-amber-400 mt-1.5">
                    Pool has {result.total_entries} entries — only the first {result.entries_imported + result.entries_skipped} were imported.
                  </div>
                )}
                {result.entries_skipped > 0 && (
                  <div className="text-sm text-muted-foreground mt-1.5">
                    {result.entries_skipped} bracket{result.entries_skipped !== 1 ? 's' : ''} skipped
                    {result.skip_reasons && result.skip_reasons.length > 0 && (
                      <ul className="mt-1 ml-5 list-disc">
                        {result.skip_reasons.map((reason, i) => (
                          <li key={i} className="text-xs text-muted-foreground">{reason}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Result */}
          {errorMessage && !isImporting && (
            <div className="mt-5 rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive text-sm">
              {errorMessage}
            </div>
          )}
        </div>

        {/* Footer */}
        {showOk && (
          <DialogFooter className="px-5 py-4 border-t">
            <Button onClick={handleOk}>OK</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddPoolModal;
